import React from "react";
import { CheckCircle2, Activity, Clock, Layers, Wrench } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import QuoteForm from "@/components/site/QuoteForm";

const BENEFITS = [
  {
    icon: Clock,
    title: "Redução de downtime",
    text: "Disponibilidade de peças críticas quando e onde a operação precisa.",
  },
  {
    icon: Activity,
    title: "Continuidade operacional",
    text: "Planos de reposição programada para minimizar paradas não planejadas.",
  },
  {
    icon: Layers,
    title: "Confiabilidade técnica",
    text: "Peças certificadas, com rastreabilidade e documentação completa.",
  },
  {
    icon: Wrench,
    title: "Suporte de engenharia",
    text: "Apoio na identificação, substituição e homologação de itens.",
  },
];

const ITEMS = [
  "Sobressalentes para BOP",
  "Válvulas de segurança e componentes",
  "Kits de reparo e vedações",
  "Componentes rotativos (bombas e compressores)",
  "Peças para sistemas hidráulicos e pneumáticos",
  "Equipamentos de registro e elementos auxiliares",
];

export default function SpareParts() {
  return (
    <>
      <section className="bg-white border-b border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze">
              Linha dedicada
            </div>
            <h1 className="mt-4 font-display font-black text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              <span className="text-brand">Spare parts</span> certificados para
              equipamentos críticos.
            </h1>
            <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-2xl">
              Fornecemos peças de reposição e sobressalentes para BOP, válvulas
              de segurança, bombas, compressores e equipamentos industriais de
              alto valor. Foco em confiabilidade, rastreabilidade e prazo de
              entrega.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#form"
                data-testid="spare-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold px-7 py-4 rounded-sm hover:bg-brand-mid transition-colors border border-transparent hover:border-bronze"
              >
                Solicitar cotação de peças
              </a>
              <a
                href="#itens"
                className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-semibold px-7 py-4 rounded-sm hover:bg-surface-alt transition-colors"
              >
                Ver itens atendidos
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 rounded-sm overflow-hidden border border-surface-line">
            <img
              src="https://images.unsplash.com/photo-1769147339214-076740872485?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwc3BhcmUlMjBwYXJ0cyUyMHByZWNpc2lvbnxlbnwwfHx8fDE3NzY5NjQxODB8MA&ixlib=rb-4.1.0&q=85"
              alt="Peças mecânicas de reposição"
              className="h-full w-full object-cover"
              loading="eager"
            />
          </div>
        </div>
      </section>

      <section id="itens" className="py-20 lg:py-24 bg-surface-alt border-y border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Itens atendidos"
            title="Peças de reposição para operações industriais."
            subtitle="Trabalhamos com uma rede de fornecedores certificados para entregar o spare part correto — rápido e com documentação."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ITEMS.map((c) => (
              <div
                key={c}
                data-testid={`spare-item-${c}`}
                className="p-6 bg-white border border-surface-line rounded-sm flex items-start gap-3 hover:border-bronze/40 transition-colors"
              >
                <CheckCircle2 className="h-5 w-5 text-brand mt-0.5" />
                <span className="text-sm font-medium text-ink">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Benefícios"
            title="Valor entregue pela Wellpec em spare parts."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="p-7 bg-white border border-surface-line rounded-sm hover:border-bronze/40 transition-colors"
              >
                <div className="h-11 w-11 rounded-sm bg-surface-alt text-brand flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <h4 className="mt-4 font-display font-bold text-ink">
                  {title}
                </h4>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="form" className="py-20 lg:py-24 bg-surface-alt border-t border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Orçamento técnico"
              title="Precisa de uma peça específica?"
              subtitle="Informe part number, fabricante, equipamento ou descrição. Quanto mais detalhada a especificação, mais rápida a nossa resposta."
            />
          </div>
          <div className="lg:col-span-7">
            <QuoteForm source="spare-parts" subjectDefault="Cotação — Spare parts" />
          </div>
        </div>
      </section>
    </>
  );
}
